import { Modal } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { IState } from '../../graphql/types';
import { setCopyGiideModal } from '../../actions';
import { useMemo, useState } from 'react';

import styles from './styles.module.css';
import useGiideList from '../../hooks/useQuery/useGiideList';
import { API, graphqlOperation } from 'aws-amplify';
import { CopyGiideResponses } from '../../graphql/api';
import { copyGiide } from '../../graphql/mutations';

enum CopyState {
  confirm,
  loading,
  success,
}

const DuplicateGiideModal = () => {
  const [state, setState] = useState(CopyState.confirm);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const dispatch = useDispatch();

  const { refetch } = useGiideList(true);

  const copyGiideModalId = useSelector<IState, string | null>(
    state => state.copyGiideModal
  );

  const handleClose = () => {
    dispatch(setCopyGiideModal(null));
    setErrorMessage(null);
    setState(CopyState.confirm);
  };

  const handleConfirm = async () => {
    if (state === CopyState.success || errorMessage) {
      handleClose();
    } else {
      setErrorMessage(null);
      setState(CopyState.loading);

      try {
        await (API.graphql(
          graphqlOperation(copyGiide, {
            input: { id: copyGiideModalId },
          })
        ) as Promise<{ data: { copyGiide: CopyGiideResponses } }>);

        refetch();
        setState(CopyState.success);
      } catch (error) {
        setErrorMessage(
          'A problem occured duplicating this giide. Please contact support.'
        );

        setState(CopyState.confirm);
      }
    }
  };

  const modalText = useMemo(() => {
    if (state === CopyState.success) {
      return 'Your giide was successfully duplicated.';
    } else {
      return 'Are you sure you want to duplicate this giide? A new giide will be created on your account with the same chapters, recordings, and feedcards as the original. This may take a few seconds to complete.';
    }
  }, [state]);

  const okText = useMemo(() => {
    if (state === CopyState.success || errorMessage) {
      return 'Ok';
    } else {
      return 'Continue';
    }
  }, [state, errorMessage]);

  return (
    <Modal
      title={'Duplicate Giide'}
      cancelText={'Cancel'}
      destroyOnClose={true}
      okText={okText}
      open={copyGiideModalId !== null}
      onCancel={handleClose}
      onOk={handleConfirm}
      confirmLoading={state === CopyState.loading}
      footer={(_, { OkBtn, CancelBtn }) => (
        <>
          {state === CopyState.confirm && !errorMessage && <CancelBtn />}
          <OkBtn />
        </>
      )}
    >
      <div className={styles.bodyText}>
        {modalText}

        <div className={styles.errorText}>{errorMessage && errorMessage}</div>
      </div>
    </Modal>
  );
};

export default DuplicateGiideModal;
